import { useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import ActivityPage from "@/pages/UserHub/Activity";
import AnalyticsPage from "@/pages/UserHub/Analytics";
import InsightsPage from "@/pages/UserHub/Insights";
import { InsightsActionsSlotContext } from "./context";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

type Tab = {
  id: string;
  label: string;
  genieIcon: GenieIconName;
};

const TABS: Tab[] = [
  {
    id: "insights",
    label: "Conversational Insights",
    genieIcon: "globe",
  },
  {
    id: "conversations",
    label: "Per-call Analysis",
    genieIcon: "document",
  },
  {
    id: "analytics",
    label: "Analytics",
    genieIcon: "trending-up",
  },
  {
    id: "activity",
    label: "Activity",
    genieIcon: "sync",
  },
];

const DEFAULT_TAB = "insights";

const isValidTab = (value: string | null): value is string => {
  if (!value) return false;
  return TABS.some((t) => t.id === value);
};

const InsightsHubPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const activeTabId = isValidTab(rawTab) ? rawTab! : DEFAULT_TAB;
  const [actions, setActions] = useState<ReactNode>(null);

  const handleSelectTab = (id: string) => {
    setActions(null);
    const next = new URLSearchParams(searchParams);
    if (id === DEFAULT_TAB) {
      next.delete("tab");
    } else {
      next.set("tab", id);
    }
    setSearchParams(next, { replace: true });
  };

  const slotValue = useMemo(() => ({ setActions }), []);

  return (
    <InsightsActionsSlotContext.Provider value={slotValue}>
      <div className="min-h-screen bg-[#F9FAFC]">
        <div className="container mx-auto px-4 py-8 max-w-[1920px] w-full">
          <div className="flex items-center mb-6 sm:mb-8">
            <GenieIcon
              name="trending-up"
              size={28}
              className="h-7 w-7 md:h-8 md:w-8 mr-3 sm:mr-4"
            />
            <div>
              <h1 className="text-3xl font-bold text-[#222B45]">Insights</h1>
              <p className="text-sm md:text-base text-[#64748B] mt-1">
                What's working across your genies — trends, performance, and the audit trail.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <InsightsTabBar activeTabId={activeTabId} onSelect={handleSelectTab} />
            {actions && (
              <div className="flex sm:justify-end w-full sm:w-auto">{actions}</div>
            )}
          </div>

          <div className="mt-6">
            <TabContent tabId={activeTabId} />
          </div>
        </div>
      </div>
    </InsightsActionsSlotContext.Provider>
  );
};

const InsightsTabBar = ({
  activeTabId,
  onSelect,
}: {
  activeTabId: string;
  onSelect: (id: string) => void;
}) => {
  const tabs = useMemo(() => TABS, []);

  return (
    <div className="flex flex-wrap gap-2">
      {tabs.map((tab) => {
        const isActive = tab.id === activeTabId;

        if (isActive) {
          return (
            <button
              key={tab.id}
              onClick={() => onSelect(tab.id)}
              className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-md shadow-genie-primary/20"
            >
              <span className="flex items-center gap-2 px-3.5 py-2 rounded-[10px] bg-white text-sm font-semibold text-[#222B45]">
                <GenieIcon name={tab.genieIcon} size={16} className="h-4 w-4" />
                {tab.label}
              </span>
            </button>
          );
        }

        return (
          <button
            key={tab.id}
            onClick={() => onSelect(tab.id)}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border bg-white border-[#E5EAF0] text-gray-700 hover:border-genie-primary/40 hover:text-genie-primary transition-all"
          >
            <GenieIcon name={tab.genieIcon} size={16} className="h-4 w-4" />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

const TabContent = ({ tabId }: { tabId: string }) => {
  if (tabId === "insights") return <InsightsPage embedded view="insights" />;
  if (tabId === "conversations") return <InsightsPage embedded view="conversations" />;
  if (tabId === "analytics") return <AnalyticsPage embedded />;
  if (tabId === "activity") return <ActivityPage embedded />;
  return null;
};

export default InsightsHubPage;
